import React, { useEffect } from 'react';
import { StepsHeader } from '../../Shared/Ui/Headers/StepsHeader/StepsHeader';
import ProjectCreationSection from '../../../../Shared/Features/ProjectCreationSection/ProjectCreationSection';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BasicLoader } from '@shared/Ui/Loaders/BasicLoader/BasicLoader';
import styles from './Step1.module.css';
import TemplateMeasurements from './TemplateMeasurements/TemplateMeasurements';
import BasicButton from '../../../../Shared/Ui/Buttons/BasicButton/BasicButton';
import {
    notifyErrorCustom,
    notifySuccessCustom,
} from '@shared/Widgets/ToastNotifications/ToastNotifications';
import { useProjectStore } from '../../../../Stores/ProjectStore';
import { ROUTES } from '@configs/AppRoutes';
import { StepsViewModeEnum } from '@shared/Enums/StepsViewModeEnum';
import { CustomProjectStep1UserGuide } from '@modules/Steps/UserGuideSteps/CustomProjectStep1UserGuide';
import { useUserGuideTour } from '@shared/Hooks/useUserGuideTour';
import { UserGuideKeyEnum } from '@shared/Enums/UserGuideKeyEnum';
// import Joyride from 'react-joyride';
import { UserGuideCursor } from '@shared/Ui/UserGuideCursor/UserGuideCursor';
import { JoyrideStyles } from '@styles/GlobalJoyrideStyles/JoyrideStyles';
import { CustomProjectStep1ElementsIdsEnum } from '@modules/Steps/Shared/Enums/CustomProjectStep1ElementsIdsEnum';
import { APIProvider } from '@vis.gl/react-google-maps';
import { CustomProjectInfoViewModel } from '@viewModels/CustomProjectInfoViewModel';
import { BOMProjectExampleData } from '@mockData/BOMProjectExampleData';
import { TemplateMainInfoSection } from '@modules/Steps/Shared/Ui/Headers/TemplateMainInfoSection/TemplateMainInfoSection';
import { FragmentsViewModel } from '@viewModels/FragmentsViewModel/FragmentsViewModel';
import { useMediaType } from '@shared/Hooks/useMediaTypeHook';
import { SESSION_STORAGE_KEYS } from '@configs/SessionStorageKeys';

const GOOGLE_KEY = import.meta.env.VITE_BASE_GOOGLE_KEY;

const Step1: React.FC = () => {
    const { projectId, viewMode } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { fromTemplate } = location.state || {};
    const [isDesktop, _isTablet, _isMobile] = useMediaType();

    const { customProject, isProjectLoading, isProjectUpdateLoading } =
        useProjectStore();
    const projectViewModel = new CustomProjectInfoViewModel();
    const fragmentsViewModel = new FragmentsViewModel();

    // const steps = CustomProjectStep1UserGuide;
    //
    // const tourKey = UserGuideKeyEnum.CUSTOM_PROJECT_STEP1_GUIDE;
    // const { runTour, handleJoyrideCallback, stepIndex, cursorPositions } =
    //     useUserGuideTour(
    //         tourKey,
    //         isDesktop && viewMode === StepsViewModeEnum.PROJECT,
    //         steps
    //     );

    useEffect(() => {
        if (viewMode === StepsViewModeEnum.PROJECT) {
            projectViewModel.getCustomProject(Number(projectId));
        } else {
            projectViewModel.getExampleProject(BOMProjectExampleData);
        }
    }, []);

    const handleNextStep = async () => {
        if (viewMode === StepsViewModeEnum.PRESENTATION) {
            navigate(ROUTES.STEP2.getPath(StepsViewModeEnum.PRESENTATION), {
                state: { fromTemplate },
            });
            return;
        }

        if (!projectId) {
            return;
        }

        const response = await projectViewModel.fetchUpdateCustomProject();

        if (response.error) {
            notifyErrorCustom(response.message ?? 'Failed to update project');
            return;
        } else {
            notifySuccessCustom('The project was updated successfully');
            await fragmentsViewModel.getMinimumPriceProjectWithParsedFragments(
                Number(projectId)
            );

            sessionStorage.setItem(
                SESSION_STORAGE_KEYS.SHOULD_RELOAD_STEP2_PAGE,
                'true'
            );
            navigate(
                ROUTES.STEP2.getPath(StepsViewModeEnum.PROJECT, projectId)
            );
        }
    };

    if (isProjectLoading) return <BasicLoader />;

    return (
        <APIProvider apiKey={GOOGLE_KEY} libraries={['places']}>
            <section className={styles.section}>
                <div
                    className={styles.container}
                    id={
                        CustomProjectStep1ElementsIdsEnum.CREATE_PROJECT_DATA_SECTION
                    }
                >
                    <StepsHeader
                        title="Step 1"
                        text="Specify your project details and parameters of building"
                    />

                    <ProjectCreationSection />
                </div>
                <TemplateMainInfoSection project={customProject} />

                <div className={styles.middleText}>
                    This building project requires you to insert dimensions for
                    the following sections below:
                </div>

                <TemplateMeasurements template={customProject.templates?.[0]} />

                <div className={styles.btnBlock}>
                    <BasicButton
                        isLoading={isProjectUpdateLoading}
                        onClick={handleNextStep}
                        extraClassName={styles.nextBtn}
                        id={
                            CustomProjectStep1ElementsIdsEnum.CREATE_PROJECT_STEP1_NEXT_BUTTON
                        }
                    >
                        Next
                    </BasicButton>
                </div>
            </section>
            {/*<Joyride*/}
            {/*    steps={steps}*/}
            {/*    run={runTour}*/}
            {/*    stepIndex={stepIndex}*/}
            {/*    spotlightClicks={true}*/}
            {/*    continuous={false}*/}
            {/*    disableOverlayClose={false}*/}
            {/*    callback={handleJoyrideCallback}*/}
            {/*    showProgress={false}*/}
            {/*    showSkipButton={true}*/}
            {/*    styles={JoyrideStyles}*/}
            {/*/>*/}
            {/*{runTour &&*/}
            {/*    cursorPositions.length > 0 &&*/}
            {/*    cursorPositions.map((position, index) => (*/}
            {/*        <UserGuideCursor*/}
            {/*            key={index}*/}
            {/*            top={position.top}*/}
            {/*            left={position.left}*/}
            {/*        />*/}
            {/*    ))}*/}
        </APIProvider>
    );
};

export default React.memo(Step1);
